<template>
  <div>
    <create-tab-modal
      ref="createTabModal"
      :selectedGuests="newTabGuests"
      :createTab="createNewTab"
      :createGuest="createGuest"
      :guests="taggedGuests"
    />

    <add-to-tab-modal
      ref="addToTabModal"
      :tabs="tabs"
      :addToTab="mergeIntoTab"
    />

    <collect-payment-modal
      ref="paymentModal"
      :allowPartial="!!tab.id"
      :payer="selectedGuest"
      :tab="tab"
      :loading="isRequestingPayment"
      @submit-payment="initiateCharge"
      @submit-adjustment="updateCurrentTab"
    />

    <terminal-status-modal />

    <b-modal
      v-model="cardReaderIsRequesting"
      :title="cardReaderTitle"
      modal-class="terminal-modal"
      hide-footer
      centered
      size="sm"
      @hidden="cardReaderInput = ''"
    >
      {{ cardReaderMessage }}
      <input
        v-model="cardReaderInput"
        class="hidden-card-input"
        ref="cardInput"
      />
      <div v-if="!validCardDetails" class="focused-indicator">
        <b>Ready to swipe</b>
        <img
          class="swipe-image"
          :src="require('@/assets/gifs/swipegif1.gif')"
        />
      </div>

      <div v-if="!validCardDetails" class="not-focused-indicator">
        <span>Reader Disengaged.</span>
        <b-btn variant="primary" @click="selectCardInput">Engage Reader</b-btn>
      </div>

      <section class="card-details">
        <br /><b>Card Number:</b>
        <span class="card-values">{{ cardReaderNumber }}</span> <br /><b
          >Exp Month:</b
        >
        <span class="card-values">{{ cardReaderMonth }}</span> <br /><b
          >Exp Year:</b
        >
        <span class="card-values">{{ cardReaderYear }}</span>
      </section>

      <section v-if="cardReaderPaymentInProcess">
        <spinner-loader :loading="cardReaderPaymentInProcess" />
      </section>

      <div class="mb-2" v-if="validCardDetails">
        <b-btn
          variant="danger"
          :disabled="cardReaderPaymentInProcess"
          @click="selectCardInput"
        >
          Reset
        </b-btn>

        <b-btn
          variant="success"
          :disabled="cardReaderPaymentInProcess"
          @click="cardReaderCharge"
        >
          Charge
        </b-btn>
      </div>

      <b-btn
        v-if="devMode"
        variant="info"
        :disabled="cardReaderPaymentInProcess"
        @click="testCard"
      >
        Test Card
      </b-btn>
    </b-modal>

    <!-- Loading indicator while items are being fetched -->
    <section v-if="isRequestingItems" class="content-container">
      <spinner-loader :loading="true" />
    </section>

    <!-- No Inventory Items Yet -->
    <section v-else-if="showNoInventory" class="content-container">
      <br />
      <br />
      <br />
      <p>
        You don't have any items in your inventory yet.<br />
        Create your first one by clicking "New Item."
      </p>
      <div>
        <b-btn @click="goToInventory">Create your inventory here</b-btn>
      </div>
    </section>

    <section v-else class="display-content content-container">
      <div
        v-if="!selectedTab.id && allowWalkin === false"
        class="tab-selection-screen"
      >
        <div class="main-pos-display full-length">
          <item-display
            :selectItem="loadTab"
            :items="tabs"
            :filterIds="activeTabFilter"
          />
        </div>

        <div v-if="tabsLoading" class="tabs-loading">
          <spinner-loader :loading="tabsLoading" />
        </div>

        <div v-else class="left-pos-display">
          <div class="start-buttons">
            <h4 class="center">{{ $t("pos_subtitle") }}</h4>
            <div class="button-group">
              <b-btn
                class="btn-lg long-button"
                variant="secondary"
                @click="onClickQuickSale"
              >
                {{ $t("pos_quick_sale") }}
              </b-btn>

              <b-btn
                class="btn-lg long-button"
                variant="secondary"
                v-b-modal.addToTabModal
              >
                {{ $t("pos_hotel_guests") }}
              </b-btn>

              <b-btn
                class="btn-lg long-button"
                variant="secondary"
                v-b-modal.createTabModal
              >
                {{ $t("pos_all_contacts") }}
              </b-btn>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="active-pos">
        <div class="main-pos-display">
          <item-display
            :selectItem="addItem"
            :items="items"
            :filterIds="itemTagFilter"
          />
        </div>
        <div class="category-display">
          <div v-if="null === selectedMenu">
            <div class="menu-category-header">
              <span style="margin: 12px; font-size: 20pt"></span>
            </div>

            <b-button
              v-for="menu in filteredMenus"
              :key="menu.id"
              style="margin: 8px"
              size="lg"
              @click="selectMenu(menu)"
            >
              {{ menu.name }}
            </b-button>
          </div>

          <div v-else>
            <div class="menu-category-header">
              <b-button-group>
                <b-button variant="dark" @click="clearMenu">
                  <i class="fi fi-arrow-left"></i>
                </b-button>
                <b-button variant="secondary" @click="selectMenu(selectedMenu)">
                  {{ selectedMenu.name }}
                </b-button>
                <b-button v-if="null != selectedCategory" variant="dark">
                  {{ selectedCategory.name }}
                </b-button>
              </b-button-group>
            </div>

            <b-button
              v-for="category in selectedMenu.menu_tag"
              :key="category.id"
              style="margin: 8px"
              size="lg"
              @click="selectCategory(category)"
            >
              {{ category.name }}
            </b-button>
          </div>
        </div>

        <div v-if="tabsLoading" class="tabs-loading">
          <spinner-loader :loading="tabsLoading" />
        </div>

        <div v-else class="left-pos-display">
          <div v-if="disabledLoading" class="tabs-loading">
            <spinner-loader :loading="disabledLoading" />
          </div>
          <!-- TODO: Add ability to hover on charge and it add a border to that item in the POS -->
          <b-btn class="return-button" @click="freshTab">
            <i class="fi fi-arrow-left"></i>
          </b-btn>

          <div class="tab-name">{{ selectedGuest }}</div>

          <guest-tab
            ref="guestTab"
            :class="
              tab.tab_item.length > 0 && !tab.id ? 'quick-sale' : 'add-item'
            "
            :tab="tab"
            :showTax="true"
            :removeCharge="removeItem"
            :showDescription="false"
            alwaysShowTips
          />

          <div class="tab-buttons">
            <div class="button-group">
              <b-row v-if="selectedTab.tab_item.length > 0">
                <b-col>
                  <b-btn
                    v-b-modal.addToTabModal
                    ariant="primary"
                    :class="['large-command-btn', { hidden: !!selectedTab.id }]"
                    :disabled="selectedTab.tab_item.length === 0"
                  >
                    {{ $t("pos_add_tab") }}
                  </b-btn>
                </b-col>

                <b-col>
                  <b-btn
                    v-b-modal.createTabModal
                    variant="outline-primary"
                    :class="{
                      hidden: !!selectedTab.id,
                      'large-command-btn': true,
                    }"
                    :disabled="selectedTab.tab_item.length === 0"
                  >
                    {{ $t("pos_start_tab") }}
                  </b-btn>
                </b-col>
              </b-row>

              <b-row
                v-if="
                  selectedTab.id &&
                  selectedTab.stats.balance_due <= 0 &&
                  selectedTab.stats.payments_made > 0
                "
              >
                <b-col>
                  <b-btn
                    class="medium-command-btn"
                    variant="outline-success"
                    :disabled="disabledLoading"
                    @click="closeCurrentTab"
                  >
                    Close Tab
                  </b-btn>
                </b-col>
              </b-row>

              <b-row v-else>
                <b-col v-if="selectedTab.tab_item.length > 0">
                  <b-btn
                    v-b-modal.collectPaymentModal
                    class="medium-command-btn mt-2"
                    variant="dark"
                  >
                    {{ $t("pos_settle_payment") }}
                  </b-btn>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import cloneDeep from "lodash.clonedeep";
import PaymentMixin from "@/mixins/payment-mixin";
import TerminalMixin from "@/mixins/terminal-mixin";

import {
  PAYMENT_TYPES,
  PAYMENT_METHODS,
  ADJUSTMENT_TYPES,
} from "@/config/constants";

import { tabNameFormatter } from "@/utils/tab-name-formatter";

import { ITEM_TYPES } from "@/config/constants";

import { mapGetters, mapState, mapActions } from "vuex";

import {
  dispatchMakePaymentEvent,
  addPaymentCompletedEvent,
  removePaymentCompletedEvent,
} from "@/utils/events";

export default {
  name: "PointOfSale",

  components: {
    ItemDisplay: () => import("@/components/pos/ItemDisplay"),
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
    GuestTab: () => import("@/components/GuestTab"),
    CreateTabModal: () => import("@/components/modals/CreateTabModal"),
    CollectPaymentModal: () =>
      import("@/components/modals/CollectPaymentModal"),
    AddToTabModal: () => import("@/components/modals/AddToTabModal"),
    TerminalStatusModal: () =>
      import("@/components/modals/TerminalStatusModal"),
  },

  mixins: [TerminalMixin, PaymentMixin],

  data() {
    return {
      items: [],
      tabs: [],
      adjustments: [],
      menus: [],
      tab: {},
      selectedTab: {},
      allowWalkin: false,
      tabsLoading: false,
      disabledLoading: false,
      selectedMenu: null,
      selectedCategory: null,
      selectedGuest: "Quick Tab",
      newTabGuests: [],
      chargeCount: 0,
      itemTagFilter: [],
      activeTabFilter: [],
      cardReaderIsRequesting: false,
      cardReaderMessage: "",
      cardReaderInput: "",
      pendingAmount: null,
      devMode: process.env.NODE_ENV !== "production",
      cardReaderPaymentInProcess: false,
      cardReaderTitle: "Card Reader Initialized",
    };
  },

  created() {
    addPaymentCompletedEvent(this.onPaymentCompleted);
  },

  beforeDestroy() {
    removePaymentCompletedEvent(this.onPaymentCompleted);
  },

  async mounted() {
    this.$Progress.start();

    await Promise.all([
      this.getAllOpenActiveTabs(),
      this.getItems(),
      this.getMenus(),
    ]);

    this.menus = this.getAvailableMenus;
    this.$Progress.finish();

    this.resetTabs();
    this.initSelectedTab();
    this.items = this.getAvailableItemsUnclassified;
    this.adjustments = this.getAvailableModifiers;
  },

  computed: {
    ...mapState({
      isRequestingItems: (state) => state.items.isRequestingItems,
      isRequestingTabs: (state) => state.tabs.isRequesting,
      isRequestingMenus: (state) => state.menus.isRequesting,
      terminalRequesting: (state) => state.terminal.terminalRequesting,
      terminalMessage: (state) => state.terminal.terminalMessage,
      vendorId: (state) => state.vendor.vendor.id,
      stripeConnected: (state) => !!state.vendor.vendor.stripe_id,
    }),

    ...mapGetters([
      "getAvailableItemsUnclassified",
      "getAvailableModifiers",
      "getOpenActiveTabs",
      "getContacts",
      "getAvailableMenus",
      "getPaymentTypes",
      "getTerminal",
      "getReaderConnectionStatus",
      "getVendor",
    ]),

    cardReaderObject() {
      const track1 = this.cardReaderInput.match(
        /%B([0-9]+)\^([A-Z /.]+)\/([A-Z /.]*)\^([0-9]{2})([0-9]{2})/
      );
      const track2 = this.cardReaderInput.match(/;\d{0,19}=\d{7}\w*\?/);

      if (track1) {
        const card = track1[1];
        const month = track1[track1.length - 1];
        const year = track1[track1.length - 2];

        return { card, month, year };
      } else if (track2) {
        const card = track2[0].substr(1, 16);
        const month = track2[0].substr(20, 2);
        const year = track2[0].substr(18, 2);

        return { card, month, year };
      } else {
        return {};
      }
    },

    cardReaderNumber() {
      return this.cardReaderObject.card;
    },

    cardReaderMonth() {
      return this.cardReaderObject.month;
    },
    cardReaderYear() {
      return this.cardReaderObject.year;
    },

    validCardDetails() {
      return (
        this.cardReaderNumber && this.cardReaderMonth && this.cardReaderYear
      );
    },

    taggedGuests() {
      return this.getContacts.map((guest) => {
        return {
          id: guest.id,
          label: `${guest.first_name} ${guest.last_name}`,
        };
      });
    },

    filteredMenus: function () {
      return this.menus.filter((menu) => menu.is_active === 1);
    },

    showNoInventory() {
      return !this.isRequestingItems && this.items.length < 1;
    },
  },

  methods: {
    ...mapActions([
      "closeTab",
      "addTabItem",
      "createTab",
      "addContact",
      "getTab",
      "getMenus",
      "deleteTabItem",
      "getAllOpenActiveTabs",
      "getItems",
      "addStripeToken",
      "processPayment",
      "addTabAdjustment",
    ]),

    initSelectedTab() {
      this.selectedTab = {
        id: 0,
        booking_id: 0,
        guest: null,
        guest_id: 0,
        tab_item: [],
        tab_modifier: [],
        stats: {
          invoice_list: [],
          taxes_total: 0,
          gratuity: 0,
          refunded: 0,
          discounts: 0,
          payments_made: 0,
          sub_total: 0,
          balance_due: 0,
        },
      };
    },

    onClickQuickSale() {
      this.initSelectedTab();
      this.allowWalkin = true;
      this.selectedMenu = null;
    },

    updateTabOwner() {
      //TODO - This is gonna be important
    },

    updateCurrentTab(tabItemData) {
      this.updateTab(tabItemData, this);
    },

    resetTabs() {
      this.tabs = tabNameFormatter(this.getOpenActiveTabs, this.getContacts);
    },

    async closeCurrentTab() {
      this.disabledLoading = true;

      this.closeTab(this.selectedTab.id)
        .then(() => {
          this.successNotification(`${this.selectedGuest}'s tab closed.`);
          this.freshTab();
        })
        .catch(() => {
          this.errorNotification(
            `Failed to close ${this.selectedGuest}'s tab.`
          );
        })
        .finally(() => {
          this.disabledLoading = false;
        });
    },

    goToInventory() {
      this.$router.push({
        path: `/${this.$route.params.locale}/settings/inventory`,
      });
    },

    mergeIntoTab(tab) {
      let tabPromises;

      tabPromises = this.selectedTab.tab_item.map((charge) => {
        this.tabsLoading = true;

        return this.addTabItem({
          item_id: charge.item_id,
          tab_id: tab.id,
          description: charge.description,
          rate: charge.rate,
        });
      });

      if (this.selectedTab.stats.gratuity) {
        const gratuityPromise = this.addTabAdjustment({
          description: "Gratuity from tab merge",
          modifier_id: this.findPaymentType(ADJUSTMENT_TYPES.GRATUITY, this),
          rate: this.selectedTab.stats.gratuity,
          tab_id: tab.id,
        });

        tabPromises.push(gratuityPromise);
      }

      if (this.selectedTab.stats.refunded) {
        const refundPromise = this.addTabAdjustment({
          description: "Refund from tab merge",
          modifier_id: this.findPaymentType(ADJUSTMENT_TYPES.REFUND, this),
          rate: this.selectedTab.stats.refunded,
          tab_id: tab.id,
        });

        tabPromises.push(refundPromise);
      }

      Promise.all(tabPromises).then((tabItems) => {
        this.$refs.addToTabModal.hide();
        this.selectedTab = tab;
        this.loadTab(tab);
      });
    },

    async createNewTab() {
      let newGuest = this.$refs.createTabModal.selectedGuestIds[0];
      let tabModifiers = [];

      if (this.selectedTab.stats.gratuity) {
        tabModifiers.push({
          description: "Gratuity from tab merge",
          modifier_id: this.findPaymentType(ADJUSTMENT_TYPES.GRATUITY, this),
          rate: this.selectedTab.stats.gratuity,
        });
      }

      if (this.selectedTab.stats.refunded) {
        tabModifiers.push({
          description: "Refund from tab merge",
          modifier_id: this.findPaymentType(ADJUSTMENT_TYPES.REFUND, this),
          rate: this.selectedTab.stats.refunded,
        });
      }

      const newTab = {
        guest_id: newGuest.id,
        tab_item: this.selectedTab.tab_item,
        is_open: true,
        tab_modifier: tabModifiers,
      };

      this.tabsLoading = true;

      let createdTab = null;

      try {
        createdTab = await this.createTab(newTab);
        this.successNotification(`Tab created for ${newGuest.label}.`);
      } catch (e) {
        this.errorNotification(`Failed to create tab for ${newGuest.label}.`);
        this.tabsLoading = false;
        return;
      }

      this.tabs.push({
        id: createdTab.id,
        name: newGuest.label,
        guest_id: newGuest.id,
      });

      this.selectedTab = createdTab;

      this.loadTab({
        id: createdTab.id,
        name: newGuest.label,
      });

      this.$refs.createTabModal.hide();

      this.resetTabs();
      this.tabsLoading = false;
    },

    createGuest(newGuestName) {
      const newGuest = {
        first_name: newGuestName.split(" ")[0],
        last_name: newGuestName.split(" ")[1],
      };

      if (!newGuest.first_name || !newGuest.last_name) {
        this.errorNotification("Quick Add Failed - Invalid Name");
        return;
      }

      this.$refs.createTabModal.hide();

      return this.addContact(newGuest)
        .then((res) => {
          this.$refs.createTabModal.addGuest({
            id: res.id,
            label: `${res.first_name} ${res.last_name}`,
            image: res.photo_md_uri || res.photo_uri,
          });

          this.createNewTab();
        })
        .catch((error) => {
          this.errorNotification(`Quick Add Failed - ${error}`);
        });
    },

    selectCategory(category) {
      this.selectedCategory = category;
      this.itemTagFilter = [category.tag_id];
    },

    selectMenu(menu) {
      this.selectedMenu = menu;
      this.selectedCategory = null;
      this.itemTagFilter = [];

      this.selectedMenu.menu_tag.map((menu_tag) => {
        this.itemTagFilter.push(menu_tag.tag_id);
      });
    },

    clearMenu() {
      this.selectedMenu = null;
      this.itemTagFilter = [];
    },

    loadTab(selection) {
      if (selection.id === 0) {
        if (this.selectedTab.id !== selection.id) {
          //only do something if it's a different tab
          this.freshTab();
        }
      } else {
        if (!this.selectedTab.id && this.selectedTab.tab_item.length > 0) {
          this.errorNotification(`You must clear or save tab.`);
        } else {
          this.tabsLoading = true;
          this.selectedGuest = selection.name;

          this.getTab(selection.id)
            .then((tab) => {
              this.selectedTab = tab;
            })
            .finally(() => {
              this.tabsLoading = false;
            });
        }
      }
    },

    freshTab() {
      this.pendingAmount = 0;
      this.selectedGuest = "Quick Tab";
      this.cardReaderIsRequesting = false;
      this.cardReaderPaymentInProcess = false;
      this.cardReaderTitle = "Card Reader Initialized";
      this.allowWalkin = false;

      this.resetTabs();
      this.initSelectedTab();
    },

    async createWalkinTab() {
      let createdTab = null;

      try {
        let tab_modifiers = [];

        if (this.selectedTab.stats.gratuity) {
          tab_modifiers.push({
            description: "Gratuity",
            modifier_id: this.findPaymentType(ADJUSTMENT_TYPES.GRATUITY, this),
            rate: this.selectedTab.stats.gratuity,
          });
        }

        if (this.selectedTab.stats.refunded) {
          tab_modifiers.push({
            description: "Refund",
            modifier_id: this.findPaymentType(ADJUSTMENT_TYPES.REFUND, this),
            rate: this.selectedTab.stats.refunded,
          });
        }

        createdTab = await this.createTab({
          tab_item: this.selectedTab.tab_item,
          tab_modifier: [...this.selectedTab.tab_modifier, ...tab_modifiers],
          is_open: true,
        });

        this.successNotification(`Walk-in bill started`);
      } catch (e) {
        this.errorNotification(`Failed to start Walk-in bill`);
        return;
      }

      const walkinTab = {
        name: "Walk In",
        id: createdTab.id,
      };

      this.tabs.push(walkinTab);
      this.selectedTab = createdTab;
      this.selectedGuest = "Walk In";
    },

    async initiateCharge(paymentData) {
      if (
        !this.cardReaderInput &&
        paymentData.paymentMethodId === PAYMENT_METHODS.CARD_READER
      ) {
        this.cardReaderIsRequesting = true;
        this.pendingAmount = paymentData.amount;
        return;
      }

      if (!this.selectedTab.id) {
        await this.createWalkinTab();
      }

      switch (paymentData.paymentMethodId) {
        case PAYMENT_METHODS.TERMINAL:
          const charged = await this.terminalCharge(paymentData);

          if (!charged) {
            return;
          }

          await this.addPayment(paymentData, this);
          break;

        case PAYMENT_METHODS.CARD_READER:
          await this.cardReaderCharge(paymentData);
          break;

        case PAYMENT_METHODS.IOS_TERMINAL:
          this.tabsLoading = true;

          dispatchMakePaymentEvent({
            ...paymentData,
            currency: "USD",
          });

          break;

        default:
          this.tabsLoading = true;
          await this.addPayment(paymentData, this);
          this.tabsLoading = false;
      }

      if (this.tabsLoading) {
        return;
      }

      if (
        !this.selectedTab.booking_id &&
        this.selectedTab.stats.balance_due <= 0
      ) {
        this.closeCurrentTab();
      } else {
        // this.freshTab();
      }

      this.$refs.paymentModal.hide();
    },

    selectCardInput() {
      this.cardReaderInput = "";
      this.$refs.cardInput.focus();
    },

    testCard() {
      this.cardReaderInput =
        "%B4242424242424242^SMITH/JOHN^25121021473810559010203?;5581123456781323=160710212423468?";
      const cardData = {
        card_number: "4242424242424242",
        card_exp_month: "12",
        card_exp_year: "25",
      };
    },

    async cardReaderCharge(paymentData) {
      this.cardReaderPaymentInProcess = true;
      this.cardReaderTitle = "Payment In Progress";

      if (!this.selectedTab.id) {
        await this.createWalkinTab();
      }

      const cardData = {
        card_number: this.cardReaderNumber,
        card_exp_month: this.cardReaderMonth,
        card_exp_year: this.cardReaderYear,
      };

      let stripeToken = {};

      try {
        stripeToken = await this.addStripeToken(cardData);
      } catch (e) {
        this.errorNotification(e || "Card Failure");
        this.cardReaderPaymentInProcess = false;
        this.cardReaderTitle = "Card Reader Initialized";
        return;
      }

      this.successNotification("Card Read Successfully");

      const chargeData = {
        token: stripeToken.id,
        vendor_id: this.vendorId,
        amount: parseInt(this.pendingAmount * 100),
        currency: "usd",
        tab_id: this.selectedTab.id,
      };

      return await this.sendCharge(chargeData);
    },

    async sendCharge(chargeData) {
      let paymentResult = {};
      let currency = this.getVendor.local_currency;

      try {
        paymentResult = await this.processPayment(chargeData);

        this.infoNotification(
          `${currency}${this.pendingAmount} Payment successfully added to bill for ${this.selectedGuest}.`
        );

        this.tabsLoading = true;

        this.getTab(chargeData.tab_id)
          .then((tab) => {
            this.selectedTab = tab;
          })
          .finally(() => {
            this.tabsLoading = false;
            this.$refs.paymentModal.hide();
          });
      } catch (e) {
        this.errorNotification(e || "Error occured while processing payment");
        paymentResult = null;
      }

      // this.freshTab();
      this.pendingAmount = 0;
      this.cardReaderIsRequesting = false;
      this.cardReaderPaymentInProcess = false;
      this.cardReaderTitle = "Card Reader Initialized";

      return paymentResult;
    },

    addItem(item) {
      const {
        id: item_id,
        category_id,
        name,
        description,
        default_rate: rate,
      } = item;

      const addedTax = item.tax_total || 0;

      const addedItem = {
        item_id,
        category_id,
        name,
        description,
        rate,
        order: this.chargeCount++,
      };

      if (!this.selectedTab.id) {
        this.selectedTab.tab_item.push(addedItem);
        this.selectedTab.stats.taxes_total += parseFloat(addedTax);
        this.selectedTab.stats.sub_total += parseFloat(rate);
        this.selectedTab.stats.balance_due +=
          parseFloat(rate) + parseFloat(addedTax);
      } else {
        const tabItem = {
          tab_id: this.selectedTab.id,
          item_id,
          rate,
          description,
        };
        // this.tabsLoading = true
        this.addTabItem(tabItem)
          .then((updatedTab) => {
            // this.tabsLoading = false
            this.selectedTab = updatedTab;

            this.infoNotification(
              `${name} added to bill for ${this.selectedGuest}`
            );
          })
          .catch((err) => {
            this.errorNotification(
              `Failed to add ${name} to bill for ${this.selectedGuest}`
            );
          });
      }
    },

    removeItem(item) {
      let chargeIndex = 0;

      const itemInfo = this.items.find(
        (availableItem) => availableItem.id === item.item_id
      );

      if (item.category_id === ITEM_TYPES.ACCOMMODATIONS) {
        this.errorNotification(`Cannot remove accomodation via POS.`);
        return item;
      }

      itemInfo.tax_total = itemInfo.tax_total
        ? parseFloat(itemInfo.tax_total)
        : 0;

      itemInfo.default_rate = itemInfo.default_rate
        ? parseFloat(itemInfo.default_rate)
        : 0;

      if (!this.selectedTab.id) {
        chargeIndex = this.selectedTab.tab_item.findIndex(
          (charge) => charge.order === item.order
        );

        this.selectedTab.tab_item.splice(chargeIndex, 1);
        this.selectedTab.stats.taxes_total -= itemInfo.tax_total;
        this.selectedTab.stats.sub_total -= itemInfo.default_rate;
        this.selectedTab.stats.balance_due -=
          itemInfo.default_rate + itemInfo.tax_total;
      } else {
        chargeIndex = this.selectedTab.tab_item.findIndex(
          (charge) => charge.id === item.id
        );

        // this.tabsLoading = true
        this.deleteTabItem(item)
          .then((tab) => {
            // this.tabsLoading = false
            this.selectedTab = tab;
            this.infoNotification(
              `${item.name} removed from bill for ${this.selectedGuest}`
            );
          })
          .catch((err) => {
            this.errorNotification(
              `Failed to remove ${item.name} from bill for ${this.selectedGuest}`
            );
          });
      }
    },

    async onPaymentCompleted(event) {
      console.log("POS > Payment Completed event", event.detail);
      let event_data = event.detail;

      this.tabsLoading = false;

      if (event_data) {
        if (event_data.success) {
          await this.addPayment(event_data, this);

          if (
            !this.selectedTab.booking_id &&
            this.selectedTab.stats.balance_due <= 0
          ) {
            this.closeCurrentTab();
          } else {
            // this.freshTab();
          }
        } else {
          this.errorNotification(
            `Payment is failed: ${event_data.reason || "unknown error"}`
          );
        }
      } else {
        this.errorNotification(`Payment is failed: received unknown event`);
      }
    },
  },

  watch: {
    getOpenActiveTabs: {
      handler(openActiveTabs, oldValue) {
        this.tabs = tabNameFormatter(openActiveTabs, this.getContacts);
      },
    },

    cardReaderIsRequesting: {
      handler(isRequesting) {
        if (isRequesting) {
          setTimeout(() => {
            this.selectCardInput();
          }, 250);
        }
      },
    },

    selectedTab: {
      deep: true,
      handler: function () {
        if (!this.selectedTab.stats) {
          return;
        }

        this.selectedTab.stats.balance_due = this.selectedTab.stats
          ? Math.round(
              parseFloat(this.selectedTab.stats.balance_due || 0) * 100
            ) / 100
          : 0;

        this.tab = cloneDeep(this.selectedTab);
      },
    },
  },
};
</script>

<style lang="scss">
.hidden-card-input {
  width: 0px;
  border: 0px;
}

.swipe-image {
  height: 100px;
  width: 200px;
}

.hidden-card-input:not(:focus) ~ .focused-indicator {
  display: none;
}
.hidden-card-input:focus ~ .not-focused-indicator {
  display: none;
}

.card-details {
  margin-bottom: 45px;
  text-align: left;

  .card-values {
    float: right;
  }
}

section.disabled-backdrop {
  background: black;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  opacity: 0.2;
}

.display-content {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
  display: flex;
  margin: 0;
  text-align: left;

  &.content-container {
    padding: 0;
    margin: 0;
  }

  .guest-tab {
    &.add-item {
      .guest-tab-window {
        max-height: calc(100vh - 235px);
      }
    }
    &.quick-sale {
      .guest-tab-window {
        max-height: calc(100vh - 300px);
      }
    }
  }

  .main-pos-display {
    position: absolute;
    right: 0px;
    top: 0px;
    width: calc(100% - 22rem);
    height: calc(100% - 12rem);
    float: right;
    background-color: #777777;
    padding: 2px 4px;
    overflow-y: scroll;
  }

  .main-pos-display.full-length {
    height: 100%;
  }

  .category-display {
    position: absolute;
    width: calc(100% - 22rem);
    height: 12.15rem;
    bottom: 0px;
    right: 0px;
    overflow: scroll;
    background-color: #bbb;
    padding: 4px;
    padding-top: 64px;
    align-self: flex-end;
    overflow-x: scroll;
  }

  .menu-category-header {
    position: absolute;
    width: 100%;
    left: 0px;
    top: 0px;
    padding: 10px;
  }

  .left-pos-display {
    height: 100%;
    width: 20rem;
    float: left;
    padding: 30px 0px 20px 10px;
    margin: auto;
    position: absolute;
    left: 10px;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    margin: 0;

    .start-buttons {
      width: 100%;
      padding: 0 15px;
      text-align: center;

      .button-group {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        margin-top: 20px;

        .long-button {
          width: 100%;
          margin: 0;
        }
      }
    }
  }

  .tabs-loading {
    position: absolute;
    top: 0px;
    left: 54px;
    width: 22rem;
    height: 100%;
  }

  .tab-buttons {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    .medium-command-btn {
      width: 100%;
      height: 50px;
    }

    .large-command-btn {
      width: 100%;
      height: 50px;

      button.disabled {
        cursor: not-allowed;
      }
    }

    .button-group {
      display: block;
      width: 100%;
      margin-top: 15px;
      margin-bottom: 15px;

      button {
        margin: 0 10px;
      }
    }
  }
  .terminal-modal {
    .modal-body {
      height: 300px;
    }
  }

  .tab-selection-screen {
    height: 100%;

    h4.center {
      text-align: center;
    }

    .long-button {
      width: 100%;
    }

    .start-buttons {
      top: 30%;
      position: absolute;
      left: 0;
      right: 0;

      .button-group {
        margin-top: 15px;

        button {
          margin-top: 25px;
        }
      }
    }
  }

  .tab-name {
    text-align: center;
    display: inline-block;
    width: 75%;
    font-weight: bold;
  }
}
</style>
